import {
  BaseDataGrid,
  ButtonRow,
  DATE_FORMAT_ISO_NO_TIME,
  dayjs,
  Dayjs,
  FormButton,
  getFilteredColDefs,
  isDayjs,
  useGridServerPagination,
  useModal,
  useSearchParamsDate,
  useSearchParamsForm,
} from "@synota-io/synota-shared-ui";
import { generatePath, Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { invoiceFields } from "../fields/invoiceFields";
import { IInvoice, useInvoices } from "../../../../api/hooks/useInvoices";
import {
  DataGridProps,
  GridColumnVisibilityModel,
  GridInputRowSelectionModel,
  GridPaginationModel,
} from "@mui/x-data-grid";
import { useCallback, useEffect, useMemo, useState } from "react";
import { TableSummaryToolbar } from "../../../../shared/datagrid/TableSummaryToolbar";
import { TableDateFiltersToolbar } from "../../../../shared/datagrid/TableDateFiltersToolbar";
import { Button } from "@mui/material";
import { CONTRACT_INVOICES_INVOICE_DETAILS_PATH, CONTRACT_INVOICES_PATH } from "../../../../paths";
import { useInvoicesReport } from "../../../../api/hooks/useInvoicesReport";
import { useContractDetails } from "../../../../api/hooks/useContractDetails";
import { useAuth } from "../../../../api/auth/useAuth";
import { InvoiceActionsCell } from "../fields/InvoiceActionsCell";
import { InvoiceDetailsModal } from "./InvoiceDetailsModal";
import { ReadMoreOutlined } from "@mui/icons-material";

type Props = Pick<DataGridProps<IInvoice>, "slots" | "slotProps" | "onRowDoubleClick" | "sx"> & {
  summary?: boolean;
};

export const InvoicesTable = ({ summary, ...props }: Props) => {
  const [rowSelectionModel, onRowSelectionModelChange] = useState<GridInputRowSelectionModel>([]);
  const [columnVisibility, setColumnVisibility] = useState<GridColumnVisibilityModel>({});

  const { isAdmin } = useAuth();
  const { state, search } = useLocation();
  const { control, watch } = useSearchParamsForm<{
    start_date: Dayjs | null;
    end_date: Dayjs | null;
  }>(
    {
      defaultValues: {
        end_date: null,
        start_date: null,
      },
    },
    {
      start_date: useSearchParamsDate(),
      end_date: useSearchParamsDate(),
    },
  );

  const navigate = useNavigate();
  const modal = useModal();
  const values = watch();

  const params = useParams<{
    id: string;
    date?: string;
  }>();

  const [paginationModel, onPaginationModelChange] = useState<GridPaginationModel>(() =>
    summary
      ? { page: 0, pageSize: 5 }
      : {
          page: 0,
          pageSize: 30,
        },
  );

  const input = {
    contract_uuid: params.id || "",
    energy_date_start: values.start_date?.format(DATE_FORMAT_ISO_NO_TIME),
    energy_date_end: values.end_date?.format(DATE_FORMAT_ISO_NO_TIME),
  };

  const {
    invoices,
    hasNextPage,
    isLoading: isLoadingInvoices,
    isFetching,
  } = useInvoices({
    ...input,
    page: paginationModel.page,
    number_to_return: paginationModel.pageSize,
  });

  const { contract, isPending: isPendingContract } = useContractDetails({
    id: params.id,
  });

  const {
    download,
    isPending: isPendingReport,
    isAvailable,
  } = useInvoicesReport(input, {
    contract_name:
      contract?.supplier_contract_name ||
      contract?.consumer_contract_name ||
      contract?.utility_service_id ||
      "Contract",
  });

  const paginationProps = useGridServerPagination({
    hasNextPage,
    currentPageRowCount: invoices.length,
    paginationModel,
    onPaginationModelChange,
  });

  const onOpenDetails = useCallback(
    (date: Dayjs) => {
      if (!params.id || !date) {
        return;
      }
      navigate(
        {
          pathname: generatePath(CONTRACT_INVOICES_INVOICE_DETAILS_PATH, {
            id: params.id,
            date: date.toISOString(),
          }),
          search,
        },
        { replace: true },
      );
    },
    [navigate, params.id, search],
  );

  const columns = useMemo(() => {
    const defaultColumns = getFilteredColDefs(
      invoiceFields,
      summary ? "summary" : "table",
      summary,
    ).map((column) => ({
      ...column,
      flex: 1,
    }));

    if (isAdmin) {
      defaultColumns.push({
        field: "actions",
        headerName: "Details",
        type: "actions",
        flex: 1,
        renderCell: ({ row }: { row: IInvoice }) => (
          <InvoiceActionsCell
            onOpen={() => onOpenDetails(dayjs(row.energy_date, DATE_FORMAT_ISO_NO_TIME))}
            row={row}
          />
        ),
      });
    }

    return defaultColumns;
  }, [isAdmin, onOpenDetails, summary]);

  useEffect(() => {
    if (!modal.open && params.date && !isFetching) {
      modal.onOpen();
    }
  }, [isFetching, modal, params.date]);

  useEffect(() => {
    if (state && "selectedRow" in state && state.selectedRow) {
      onRowSelectionModelChange([state.selectedRow]);
    }
  }, [state]);

  const [lastInvoice] = invoices;
  const lastDate = lastInvoice
    ? dayjs(lastInvoice.energy_date, DATE_FORMAT_ISO_NO_TIME)
    : dayjs(contract?.begin_time, DATE_FORMAT_ISO_NO_TIME).subtract(1, "day");

  const isValidLastDate = isDayjs(lastDate);

  const isLoading = isLoadingInvoices || isPendingReport || isPendingContract;

  return (
    <>
      <BaseDataGrid
        onRowDoubleClick={({ row }) =>
          contract &&
          navigate(
            generatePath(CONTRACT_INVOICES_INVOICE_DETAILS_PATH, {
              id: contract.shared_uuid,
              date: row.energy_date,
            }),
          )
        }
        {...props}
        sx={summary ? { ...props.sx, minHeight: "280px" } : props.sx}
        slots={{
          footer: summary
            ? () => (
                <ButtonRow borderTop="solid" borderColor="background.default">
                  <Button
                    component={Link}
                    to={generatePath(CONTRACT_INVOICES_PATH, { id: params.id! })}
                    size="small"
                    fullWidth
                  >
                    See all
                  </Button>
                </ButtonRow>
              )
            : undefined,
          toolbar: summary
            ? TableSummaryToolbar
            : (props) =>
                isAdmin ? (
                  <TableDateFiltersToolbar
                    control={control}
                    isLoading={isLoading}
                    action={
                      <FormButton
                        variant="text"
                        startIcon={<ReadMoreOutlined />}
                        disabled={isLoading || !isValidLastDate}
                        onClick={() => onOpenDetails(lastDate.add(1, "day"))}
                      >
                        Simulate Next Invoice
                      </FormButton>
                    }
                    onDownloadClick={isAvailable ? download : undefined}
                    {...props}
                  />
                ) : null,
          ...props.slots,
        }}
        density={summary ? "compact" : "standard"}
        getRowId={(row) => row.shared_uuid}
        columns={columns}
        columnVisibilityModel={columnVisibility}
        onColumnVisibilityModelChange={setColumnVisibility}
        onRowSelectionModelChange={onRowSelectionModelChange}
        rowSelectionModel={rowSelectionModel}
        autosizeOnMount={!summary}
        {...paginationProps}
        loading={isLoading}
        rows={invoices}
      />
      <InvoiceDetailsModal
        key={params.date}
        contract_shared_uuid={params.id}
        energy_date={params.date}
        {...modal}
        onClose={() =>
          navigate(
            { pathname: "..", search },
            { preventScrollReset: true, relative: "path", replace: true },
          )
        }
      />
    </>
  );
};
